export const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "fbclid",
  "msclkid",
  "ttclid",
  "li_fat_id",
  "twclid",
  "epik",
  "sc_click_id",
] as const;

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
export type AttributionTouch = Partial<Record<AttributionKey, string>> & {
  captured_at: number;
};

export type AttributionSnapshot = {
  first_touch?: AttributionTouch;
  last_touch?: AttributionTouch;
};

export const ATTRIBUTION_STORAGE_KEYS = {
  first: "prospix_attrib_first",
  last: "prospix_attrib_last",
} as const;

export const ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

const PARAM_ALIASES: Record<AttributionKey, string[]> = {
  utm_source: ["utm_source"],
  utm_medium: ["utm_medium"],
  utm_campaign: ["utm_campaign"],
  utm_term: ["utm_term"],
  utm_content: ["utm_content"],
  gclid: ["gclid"],
  gbraid: ["gbraid"],
  wbraid: ["wbraid"],
  dclid: ["dclid"],
  fbclid: ["fbclid"],
  msclkid: ["msclkid"],
  ttclid: ["ttclid"],
  li_fat_id: ["li_fat_id"],
  twclid: ["twclid"],
  epik: ["epik"],
  sc_click_id: ["ScCid", "sccid", "sc_click_id"],
};

function boundedValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  if (!normalized) return undefined;
  return normalized.length > 200 ? normalized.slice(0, 200) : normalized;
}

export function readAttributionParams(url: string) {
  const params = new URL(url).searchParams;
  const output: Partial<Record<AttributionKey, string>> = {};
  for (const key of ATTRIBUTION_KEYS) {
    for (const alias of PARAM_ALIASES[key]) {
      const value = boundedValue(params.get(alias));
      if (!value) continue;
      output[key] = value;
      break;
    }
  }
  return output;
}

export function parseAttributionTouch(
  raw: string | null | undefined,
): AttributionTouch | undefined {
  if (!raw) return undefined;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return undefined;
    }
    const record = value as Record<string, unknown>;
    if (
      typeof record.captured_at !== "number" ||
      !Number.isFinite(record.captured_at) ||
      record.captured_at <= 0
    ) {
      return undefined;
    }

    const touch: AttributionTouch = { captured_at: record.captured_at };
    for (const key of ATTRIBUTION_KEYS) {
      const value = boundedValue(record[key]);
      if (value) touch[key] = value;
    }
    return touch;
  } catch {
    return undefined;
  }
}

export function isAttributionTouchFresh(
  touch: AttributionTouch | undefined,
  at = Date.now(),
): touch is AttributionTouch {
  if (!touch) return false;
  return (
    touch.captured_at <= at && at - touch.captured_at <= ATTRIBUTION_MAX_AGE_MS
  );
}

export function readAttributionSnapshot(
  storage: {
    get: (key: string) => string | null;
  },
  at = Date.now(),
): AttributionSnapshot | undefined {
  const parsedFirstTouch = parseAttributionTouch(
    storage.get(ATTRIBUTION_STORAGE_KEYS.first),
  );
  const parsedLastTouch = parseAttributionTouch(
    storage.get(ATTRIBUTION_STORAGE_KEYS.last),
  );
  const firstTouch = isAttributionTouchFresh(parsedFirstTouch, at)
    ? parsedFirstTouch
    : undefined;
  const lastTouch = isAttributionTouchFresh(parsedLastTouch, at)
    ? parsedLastTouch
    : undefined;
  if (!firstTouch && !lastTouch) return undefined;
  return {
    ...(firstTouch ? { first_touch: firstTouch } : {}),
    ...(lastTouch ? { last_touch: lastTouch } : {}),
  };
}
